import { useQueryClient } from '@tanstack/react-query'
import { MenuDescription } from '@features/sidebar/components/MenuDescription'
import { ToggleSwitch } from '@features/shared/components/ToggleSwitch'
import { settingsQueryKeys } from '@features/settings/api/queryKeys'
import { useSettingsStore } from '@features/settings/stores/useSettingsStore'
import { usePermissionBypassStore } from '../stores/usePermissionBypassStore'

/**
 * The Me menu's Bypass Permissions switch, for administrators only: while
 * it's on, every request skips permission checks. Off on every page load
 * (usePermissionBypassStore). A setting, not a page: the row has no hover of
 * its own, and reads in warning amber while it's on.
 */
export function BypassPermissionsRow({ onToggle }: { onToggle?: () => void }) {
  const isAdministrator = useSettingsStore((state) => state.isAdministrator)
  const bypassing = usePermissionBypassStore((state) => state.bypassing)
  const setBypassing = usePermissionBypassStore((state) => state.setBypassing)
  const queryClient = useQueryClient()

  if (!isAdministrator) return null

  const toggle = (next: boolean) => {
    onToggle?.()
    setBypassing(next)
    // Refresh in place: clearing cached data briefly removes permitted modules,
    // shrinks the menu, and sends mounted pages back through initial loading.
    // Inactive queries become stale and refresh when next used. Configuration
    // isn't filtered by permissions, so it stays.
    void queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.join('/') !== settingsQueryKeys.configuration.join('/'),
    })
  }

  return (
    <div
      className={`flex h-7 min-w-0 items-center gap-2 rounded-md pl-2 pr-1.5 text-xs font-normal ${
        bypassing
          ? 'bg-[color:var(--surface-warning-subtle)] text-[color:var(--text-warning)]'
          : 'bg-transparent text-[color:var(--sidebar-submenu-text-muted)]'
      }`}
    >
      <span className="truncate">Bypass Permissions</span>
      <MenuDescription description="While it's on, you see and can change everything, whatever your permission groups grant. Only administrators have it, each use is logged, and it turns off when the app reloads." />
      <ToggleSwitch
        className="ml-auto"
        checked={bypassing}
        onChange={toggle}
        ariaLabel="Bypass Permissions"
      />
    </div>
  )
}
